import { Box, Button, Typography } from "@mui/material";
import { useContext, useState } from "react";
import { StaffWorkLogsContext } from "../StaffWorkLogsDetail";
import { getEffectiveCumulativeToDate } from "../utils/calculations";
import { SummaryItem } from "./SummaryItem";
import { CumulativeHoursModal } from "./CumulativeHoursModal";
import { CumulativeBalanceSummary } from "./CumulativeBalanceSummary";
import { formatDisplayHours } from "../utils/general";

type WorkHoursSummaryProps = {
    isAdminOrCurrentUser: boolean;
    isServiceProvider: boolean;
};

export const WorkHoursSummary = ({ isAdminOrCurrentUser, isServiceProvider }: WorkHoursSummaryProps) => {
    const {
        selectedYear,
        selectedMonth,
        totals,
        staffAgreement,
        mealAllowanceDays,
        cumulativeExpected,
        cumulativeWorked,
        monthlyWorkedHours,
        dayOffConfigMissing,
        agreementTypeMissing,
        priorBalance,
        payData,
    } = useContext(StaffWorkLogsContext);
    const { hoursAvailability } = payData;

    const [isModalOpen, setIsModalOpen] = useState(false);

    const date = new Date(selectedYear, selectedMonth);

    const hasSickLeave = totals.totalSickLeaveDays > 0;
    const hasMissedHours = totals.totalMissedHours > 0;
    const hasAbsences = hasSickLeave || hasMissedHours;

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <SummaryItem
                title="Horas trabalhadas Mês"
                tooltip="Horas trabalhadas ou lançadas este mês"
            >
                <Typography variant="body1">
                    {formatDisplayHours(monthlyWorkedHours)}
                </Typography>
                {!isServiceProvider &&
                    <Typography>Dias de subsídio: {mealAllowanceDays}</Typography>
                }
            </SummaryItem>
            {hasAbsences && (
                <SummaryItem
                    title="Baixa e Faltas"
                    tooltip="Horas de baixa médica e faltas justificadas/injustificadas neste mês. Estas horas são adicionadas às horas consideradas."
                >
                    {hasSickLeave && (
                        <Typography variant="body1">
                            Baixa: {formatDisplayHours(totals.totalSickLeaveHours, 0)} ({totals.totalSickLeaveDays} {totals.totalSickLeaveDays === 1 ? "dia" : "dias"})
                        </Typography>
                    )}
                    {hasMissedHours && (
                        <Typography variant="body1">
                            Faltas: {formatDisplayHours(totals.totalMissedHours, 0)}
                        </Typography>
                    )}
                </SummaryItem>
            )}
            {!isServiceProvider && (
                <Box>
                    <Typography variant="h6" color="primary">
                        Horas consideradas vs esperadas
                    </Typography>
                    {hoursAvailability.state === "unavailable" ? (
                        <Typography variant="body1" color="error" sx={{ pt: 0.5 }}>
                            Saldo indisponível: {hoursAvailability.reason}
                        </Typography>
                    ) : hoursAvailability.state === "loading" ? (
                        <Typography variant="body1" color="text.secondary" sx={{ pt: 0.5 }}>
                            A carregar o saldo…
                        </Typography>
                    ) : dayOffConfigMissing || agreementTypeMissing ? (
                        <Typography variant="body1" color="error" sx={{ pt: 0.5 }}>
                            {agreementTypeMissing
                                ? "Tipo de contrato em falta — definir nas horas semanais."
                                : "Tipo de folga em falta — definir nas horas semanais."}
                        </Typography>
                    ) : (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, pt: 0.5 }}>
                            <CumulativeBalanceSummary
                                worked={cumulativeWorked}
                                expected={cumulativeExpected}
                                priorBalance={priorBalance}
                                cutoffDate={getEffectiveCumulativeToDate(date, staffAgreement?.staff_weekly_hours || [])}
                            />
                            <Button variant="outlined" size="small" onClick={() => setIsModalOpen(true)}>
                                Ver detalhe
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
            <CumulativeHoursModal open={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </Box>
    );
};
